import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { ToggleRouterService } from '../toggle-router.service';
import { verifyRequiredAttribute } from './toggle-point.directive';
import { DATA_ERROR, FEATURE_ID_ERROR } from '../utils/constants';

/**
 * @ignore
 *
 * Build guard response.
 *
 * @param isEnabled Flag that triggers the response
 * @param redirectPath string to build a URLTree
 * @param router Router angular service
 *
 * @return true, false or the URLTree if redirectPath exists
 */
export const buildGuardResponse = (isEnabled: boolean, redirectPath: string, router: Router): boolean | UrlTree =>
  isEnabled || (!!redirectPath && router.parseUrl(redirectPath));

/**
 * Used to pass data to the guard through the route data property.
 */
export interface TogglePointDataType {
  featureId: string | boolean;
  redirectPath?: string;
}

/**
 * Service that implements CanActivate which should be used to guard a route to
 * an experimental module or component. Data should be provided on the route
 * definition. A feature ID is required for this guard to work.
 */
/** @deprecated Use {@link togglePointGuard} functional guard instead */
@Injectable({ providedIn: 'root' })
export class TogglePointGuard {
  constructor(private toggleRouterService: ToggleRouterService, private router: Router) {}

  /**
   * Will check if the feature is enabled on the toggle router.
   *
   * @param data Property gotten form the angular route
   */
  canActivate({ data }: ActivatedRouteSnapshot): boolean | UrlTree {
    verifyRequiredAttribute(data, DATA_ERROR);
    const { featureId, redirectPath }: TogglePointDataType = data as TogglePointDataType;
    verifyRequiredAttribute(featureId, FEATURE_ID_ERROR);
    const isEnabled = this.toggleRouterService.isFeatureEnabled(featureId);

    return buildGuardResponse(isEnabled, redirectPath, this.router);
  }
}

export const togglePointGuard: CanActivateFn = (route) => {
  return inject(TogglePointGuard).canActivate(route);
};
